import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Car, Gauge, Navigation, Award } from 'lucide-react';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import ScoreCircle, { ZNAMKA_COLORS } from '../flotila/ScoreCircle';
import { SKLO_SVETLE } from '../../lib/uiTokens';

const SECTION_COLOR = '#0CC0DF';

const UDALOST_COLORS = {
    rychlost:   '#ef4444',
    brzdeni:    '#f59e0b',
    akcelerace: '#3b82f6',
    zataceni:   '#8b5cf6',
};

const UDALOST_LABELS = {
    rychlost:   'Překročení rychlosti',
    brzdeni:    'Prudké brzdění',
    akcelerace: 'Prudká akcelerace',
    zataceni:   'Ostré zatáčení',
};

const ZNAMKA_TEXT = {
    green:  'Nejlepší třetina',
    yellow: 'Prostřední třetina',
    red:    'Nejhorší třetina',
};

function formatHodnota(u) {
    const hodnota = parseFloat(u.hodnota);
    if (u.typ === 'rychlost' && u.limit_hodnota) {
        const limit = parseFloat(u.limit_hodnota);
        const nad = hodnota - limit;
        const procenta = limit > 0 ? Math.round((hodnota / limit - 1) * 100) : 0;
        return (
            <div style={{ lineHeight: 1.35 }}>
                <div>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{hodnota.toFixed(0)}</span>
                    <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>
                        {' '}/ {limit.toFixed(0)} km/h
                    </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 400 }}>
                    dosažená / povolená
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#ef4444', marginTop: 2 }}>
                    překročeno o {nad.toFixed(0)} km/h · +{procenta} %
                </div>
            </div>
        );
    }
    return hodnota.toFixed(1);
}

// Breakdown — ukazuje pro každý typ events: kolik kusů + kolik trestných bodů.
function BreakdownRow({ label, data, color }) {
    const pocet = data?.pocet ?? 0;
    const body = data?.body ?? 0;
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed var(--border-default)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
            </span>
            <span style={{ fontSize: 13, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ color: 'var(--text-tertiary)' }}>{pocet}× · </span>
                <span style={{ fontWeight: 700, color }}>{body} b.</span>
            </span>
        </div>
    );
}

/**
 * Karta řidiče — detail ve sloupci záložky Rozvoz → Řidiči (norma §1).
 * Dřív samostatná stránka `flotila-karta-ridice`; cesta zpět je teď klik
 * na seznam vedle (desktop) nebo šipka nad kartou (mobil, `onZpet`).
 */
export default function KartaRidice({ zamestnanecId, onVyberRidice, onZpet = null }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const [data, setData] = useState(null);
    const [scoringData, setScoringData] = useState(null);
    const [ridici, setRidici] = useState([]);
    const [tab, setTab] = useState('detail');
    const [loading, setLoading] = useState(true);
    const [obdobi, setObdobi] = useState(30); // 7 / 30 / 90 dní | 'custom'
    // Custom datum range — aktivní jen když obdobi === 'custom'
    const [customOd, setCustomOd] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 30);
        return d.toISOString().slice(0, 10);
    });
    const [customDo, setCustomDo] = useState(() => new Date().toISOString().slice(0, 10));

    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.10)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
    } : SKLO_SVETLE;

    useEffect(() => {
        if (!zamestnanecId) return;
        setLoading(true);
        // Custom rozsah → posíláme od/do (controller umí na všech 3 endpointech).
        // Jinak posíláme `obdobi` (dní zpět, default fallback).
        const q = obdobi === 'custom'
            ? `?od=${customOd}&do=${customDo}`
            : `?obdobi=${obdobi}`;
        Promise.all([
            fetch(`/api/flotila/ridici/${zamestnanecId}${q}`).then(r => r.json()),
            fetch(`/api/flotila/ridici/${zamestnanecId}/scoring${q}`).then(r => r.json()),
            fetch(`/api/flotila/ridici${q}`).then(r => r.json()),
        ]).then(([d, s, r]) => {
            setData(d);
            setScoringData(s);
            setRidici(r);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [zamestnanecId, obdobi, customOd, customDo]);

    if (loading) {
        return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>Načítám...</div>;
    }

    if (!data) {
        return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>Řidič nenalezen</div>;
    }

    const scoring = data.scoring || scoringData?.scoring || {};
    const trend = scoringData?.trend || [];
    const z = data.zamestnanec || {};
    const znamkaColor = ZNAMKA_COLORS[scoring.znamka] || 'var(--text-tertiary)';
    const valid = scoring.body_per_km !== null && scoring.body_per_km !== undefined;
    // Lidský label pro období (rychlovolba: "30 dní", custom: "1.5. – 14.5.")
    const obdobiLabel = obdobi === 'custom'
        ? `${customOd.slice(8, 10)}.${customOd.slice(5, 7)}. – ${customDo.slice(8, 10)}.${customDo.slice(5, 7)}.`
        : `${obdobi} dní`;

    return (
        <div style={{ padding: '0 0 32px' }}>
            {onZpet && <ZpetTlacitko onClick={onZpet} popis="Zpět na řidiče" sklo style={{ marginBottom: 8 }} />}

            {/* Profil header */}
            <div style={{ ...cardStyle, borderRadius: 9, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                    width: 56, height: 56, borderRadius: 10, background: `${SECTION_COLOR}1F`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    overflow: 'hidden',
                }}>
                    {z.foto_url ? (
                        <img src={z.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <Car size={24} color={SECTION_COLOR} />
                    )}
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{z.jmeno} {z.prijmeni}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {z.datum_nastupu ? `Ve firmě od ${new Date(z.datum_nastupu).toLocaleDateString('cs-CZ')}` : ''}
                    </div>
                    {(data.historie_km != null || data.historie_jizd != null) && (
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                            Celková historie: {data.historie_km ?? 0} km · {data.historie_jizd ?? 0} jízd
                        </div>
                    )}
                </div>
                <ScoreCircle scorePct={scoring.score_pct} bodyPerKm={scoring.body_per_km} znamka={scoring.znamka || '—'} />
            </div>

            {/* Selektor období */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
                fontSize: 12, color: 'var(--text-secondary)', flexWrap: 'wrap',
            }}>
                <span>Hodnocení za</span>
                {[7, 30, 90].map(d => (
                    <button key={d} onClick={() => setObdobi(d)} style={{
                        padding: '4px 10px', fontSize: 12, fontWeight: 600,
                        borderRadius: 5, cursor: 'pointer',
                        background: obdobi === d ? `${SECTION_COLOR}1F` : 'transparent',
                        color: obdobi === d ? SECTION_COLOR : 'var(--text-secondary)',
                        border: `1px solid ${obdobi === d ? SECTION_COLOR : 'var(--border-default)'}`,
                    }}>
                        {d} dní
                    </button>
                ))}
                <button onClick={() => setObdobi('custom')} style={{
                    padding: '4px 10px', fontSize: 12, fontWeight: 600,
                    borderRadius: 5, cursor: 'pointer',
                    background: obdobi === 'custom' ? `${SECTION_COLOR}1F` : 'transparent',
                    color: obdobi === 'custom' ? SECTION_COLOR : 'var(--text-secondary)',
                    border: `1px solid ${obdobi === 'custom' ? SECTION_COLOR : 'var(--border-default)'}`,
                }}>
                    Vlastní rozsah
                </button>
                {obdobi === 'custom' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                        <input type="date" value={customOd} onChange={e => setCustomOd(e.target.value)}
                            style={{
                                padding: '3px 6px', fontSize: 12, borderRadius: 5,
                                border: '1px solid var(--border-default)',
                                background: 'var(--surface-base)', color: 'var(--text-primary)',
                            }} />
                        <span>–</span>
                        <input type="date" value={customDo} onChange={e => setCustomDo(e.target.value)}
                            min={customOd}
                            style={{
                                padding: '3px 6px', fontSize: 12, borderRadius: 5,
                                border: '1px solid var(--border-default)',
                                background: 'var(--surface-base)', color: 'var(--text-primary)',
                            }} />
                    </span>
                )}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border-default)' }}>
                {[{ id: 'detail', label: 'Detail' }, { id: 'zebricek', label: 'Žebříček' }].map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} style={{
                        padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        background: 'none', border: 'none',
                        color: tab === t.id ? SECTION_COLOR : 'var(--text-secondary)',
                        borderBottom: `2px solid ${tab === t.id ? SECTION_COLOR : 'transparent'}`, marginBottom: -1,
                    }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'detail' && (
                <div>
                    {/* Metriky */}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                        <MetricCard icon={Navigation} label={`Km / ${obdobiLabel}`} value={`${data.km_obdobi ?? 0} km`} cardStyle={cardStyle} />
                        <MetricCard icon={Car} label={`Jízd / ${obdobiLabel}`} value={data.pocet_jizd_obdobi ?? 0} cardStyle={cardStyle} />
                        <MetricCard icon={Gauge} label="Ø km / jízda" value={
                            data.pocet_jizd_obdobi > 0 ? `${Math.round(data.km_obdobi / data.pocet_jizd_obdobi)} km` : '—'
                        } cardStyle={cardStyle} />
                        <MetricCard icon={Award} label="Trestné body" value={scoring.body_celkem ?? '—'} color={znamkaColor} cardStyle={cardStyle} />
                    </div>

                    {!valid && (
                        <div style={{ ...cardStyle, borderRadius: 9, padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
                            Za zvolené období ({obdobiLabel}) řidič neujel dost km — není z čeho počítat hodnocení.
                        </div>
                    )}

                    {valid && <>
                    {/* Souhrnný panel s percentilovým skóre a interpretací */}
                    <div style={{ ...cardStyle, borderRadius: 9, padding: 20, marginBottom: 16 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Skóre řidiče</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 16 }}>
                            Pozice v žebříčku oproti ostatním řidičům za stejné období. 100 % = nejbezpečnější, 0 % = nejhorší.
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
                            <div style={{ fontSize: 44, fontWeight: 800, color: znamkaColor, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1 }}>
                                {scoring.score_pct ?? '—'}
                                <span style={{ fontSize: 22, marginLeft: 2 }}>%</span>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                = {Number(scoring.body_per_km).toFixed(2)} b/km
                            </div>
                            {scoring.znamka && scoring.znamka !== '—' && (
                                <div style={{
                                    marginLeft: 'auto',
                                    padding: '4px 10px', borderRadius: 999,
                                    background: `${znamkaColor}1F`, color: znamkaColor,
                                    fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                                }}>
                                    {ZNAMKA_TEXT[scoring.znamka] || scoring.znamka}
                                </div>
                            )}
                        </div>

                        {/* Breakdown podle typu událostí */}
                        <BreakdownRow label="Překročení rychlosti" data={scoring.breakdown?.rychlost} color="#ef4444" />
                        <BreakdownRow label="Prudké brzdění" data={scoring.breakdown?.brzdeni} color="#f59e0b" />
                        <BreakdownRow label="Prudká akcelerace" data={scoring.breakdown?.akcelerace} color="#3b82f6" />
                        <BreakdownRow label="Ostré zatáčení" data={scoring.breakdown?.zataceni} color="#8b5cf6" />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13, fontWeight: 700 }}>
                            <span style={{ color: 'var(--text-primary)' }}>Celkem</span>
                            <span style={{ color: znamkaColor, fontVariantNumeric: 'tabular-nums' }}>{scoring.body_celkem} b. / {scoring.km_celkem} km</span>
                        </div>
                    </div>

                    {/* Trend — % skóre za 6 měsíců (vyšší sloupec = lepší pozice) */}
                    {trend.length > 0 && (
                        <div style={{ ...cardStyle, borderRadius: 9, padding: 20, marginBottom: 16 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>Trend skóre (6 měsíců)</div>
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={trend}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : '#eee'} />
                                    <XAxis dataKey="mesic" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                                        tickFormatter={v => v.slice(5)} />
                                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                                        tickFormatter={v => `${v}%`} />
                                    <Tooltip content={<TrendTooltip />} cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }} />
                                    <Bar dataKey="score_pct" radius={[4,4,0,0]}>
                                        {trend.map((d, idx) => (
                                            <Cell key={idx} fill={ZNAMKA_COLORS[d.znamka] || '#9ca3af'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Poslední události */}
                    {data.posledni_udalosti?.length > 0 && (
                        <div style={{ ...cardStyle, borderRadius: 9, overflow: 'hidden' }}>
                            <div style={{ padding: '16px 16px 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Události za období ({obdobiLabel})</div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <tbody>
                                        {data.posledni_udalosti.map(u => {
                                            const misto = [u.ulice, u.mesto].filter(Boolean).join(', ');
                                            return (
                                                <tr key={u.id} style={{ borderTop: '1px solid var(--border-default)' }}>
                                                    <td style={{ padding: '8px 16px', color: 'var(--text-secondary)', fontSize: 12, whiteSpace: 'nowrap' }}>
                                                        {new Date(u.zaznameno_at).toLocaleString('cs-CZ')}
                                                    </td>
                                                    <td style={{ padding: '8px 12px' }}>
                                                        <span style={{
                                                            padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                                                            background: `${UDALOST_COLORS[u.typ]}1F`, color: UDALOST_COLORS[u.typ],
                                                        }}>
                                                            {UDALOST_LABELS[u.typ]}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontWeight: 600 }}>
                                                        {formatHodnota(u)}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                                        <span style={{
                                                            padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                                                            background: u.body > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(155,155,155,0.12)',
                                                            color: u.body > 0 ? '#ef4444' : 'var(--text-tertiary)',
                                                            fontVariantNumeric: 'tabular-nums',
                                                        }}>
                                                            +{u.body ?? 0} b.
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>
                                                        {misto || (u.lat && u.lng ? `${parseFloat(u.lat).toFixed(4)}, ${parseFloat(u.lng).toFixed(4)}` : '—')}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>
                                                        {u.vozidlo?.spz || '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    </>}
                </div>
            )}

            {tab === 'zebricek' && (
                <div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {ridici.map((r, i) => {
                            const isMe = r.zamestnanec_id == zamestnanecId;
                            const bezDat = r.body_per_km == null || r.znamka === '—';
                            const barva = bezDat ? 'var(--text-tertiary)' : ZNAMKA_COLORS[r.znamka];
                            return (
                                <div key={r.zamestnanec_id}
                                    onClick={() => onVyberRidice?.(r.zamestnanec_id)}
                                    style={{
                                        ...cardStyle, borderRadius: 9, padding: 14, cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 12,
                                        border: isMe ? `2px solid ${SECTION_COLOR}` : cardStyle.border,
                                        opacity: bezDat ? 0.6 : 1,
                                    }}>
                                    <div style={{
                                        width: 28, height: 28, borderRadius: 6,
                                        background: !bezDat ? `${barva}1F` : 'var(--border-default)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 13, fontWeight: 700,
                                        color: !bezDat ? barva : 'var(--text-tertiary)',
                                    }}>
                                        {bezDat ? '–' : i + 1}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                                            {r.cele_jmeno}
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                                            {bezDat ? `Bez dostatku km (${r.km_celkem} km)` : `${r.body_celkem} b. · ${r.km_celkem} km · ${r.pocet_jizd} jízd`}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: barva, fontVariantNumeric: 'tabular-nums' }}>
                                            {r.score_pct != null ? `${r.score_pct}%` : '—'}
                                        </div>
                                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                                            {r.body_per_km != null ? `${Number(r.body_per_km).toFixed(2)} b/km` : '—'}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {ridici.length === 0 && (
                            <div style={{ ...cardStyle, borderRadius: 9, padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
                                Žádní řidiči s jízdami
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function TrendTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const valid = d.body_per_km != null;
    return (
        <div style={{
            background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
            borderRadius: 6, padding: '8px 12px', fontSize: 12,
            boxShadow: 'var(--shadow-sm)',
        }}>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{d.mesic}</div>
            {!valid ? (
                <div style={{ color: 'var(--text-tertiary)' }}>Bez dostatku km</div>
            ) : (
                <>
                    <div style={{ color: 'var(--text-primary)' }}>
                        <b style={{ color: ZNAMKA_COLORS[d.znamka] }}>{d.score_pct ?? '—'}%</b>
                        {' '}<span style={{ color: 'var(--text-tertiary)' }}>({Number(d.body_per_km).toFixed(2)} b/km)</span>
                    </div>
                    <div style={{ color: 'var(--text-secondary)' }}>{d.body_celkem} b. · {d.km} km · {d.pocet_jizd} jízd</div>
                </>
            )}
        </div>
    );
}

function MetricCard({ icon: Icon, label, value, color, cardStyle }) {
    return (
        <div style={{ ...cardStyle, borderRadius: 9, padding: 16, flex: '1 1 140px', minWidth: 140 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{
                    width: 32, height: 32, borderRadius: 6,
                    background: `${color || '#0CC0DF'}1F`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Icon size={16} style={{ color: color || '#0CC0DF' }} />
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
        </div>
    );
}
